<template>
  <div class="bg-gradient-to-r from-content-800 to-content-700 rounded-lg overflow-hidden hover:from-content-700 hover:to-content-600 transition-all duration-300 cursor-pointer smooth"
       @click="$emit('click', opportunity)">
    <div class="relative">
      <img 
        v-if="opportunity.images && opportunity.images.length > 0"
        :src="opportunity.images[0]" 
        :alt="opportunity.title"
        class="w-full h-48 object-cover"
        loading="lazy"
      />
      <div v-else class="w-full h-48 bg-content-600 flex items-center justify-center">
        <span class="text-content-200">Sin imagen</span>
      </div>
      
      <div class="absolute top-3 right-3 flex flex-col gap-1">
        <!-- Investment type badges -->
        <template v-if="opportunity.enableDebt && opportunity.enableCoOwnership">
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">
            Deuda
          </span>
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
            Adquisición de inmueble
          </span>
        </template>
        <template v-else>
          <span v-if="opportunity.enableDebt" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-600 text-white">
            Deuda
          </span>
          <span v-else-if="opportunity.enableCoOwnership" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-600 text-white">
            Adquisición de inmueble
          </span>
        </template>
      </div>
      
      <div class="absolute top-3 left-3 flex flex-col gap-1">
        <!-- Show both rates if both investment types are enabled -->
        <template v-if="opportunity.enableDebt && opportunity.enableCoOwnership">
          <span v-if="opportunity.annualFixedRate" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500 text-white">
            {{ opportunity.annualFixedRate }}% anual
          </span>
          <span v-if="opportunity.estimatedIRR" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">
            {{ opportunity.estimatedIRR }}% TIR
          </span>
        </template>
        <!-- Show only one rate if only one type is enabled -->
        <template v-else>
          <span v-if="opportunity.annualFixedRate" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-500 text-white">
            {{ opportunity.annualFixedRate }}% anual
          </span>
          <span v-else-if="opportunity.estimatedIRR" class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-500 text-white">
            {{ opportunity.estimatedIRR }}% TIR
          </span>
        </template>
      </div>
    </div>

    <div class="p-6">
      <div class="mb-4">
        <h3 class="text-lg font-semibold text-white mb-2 line-clamp-2">
          {{ opportunity.title }}
        </h3>
        <p class="text-sm text-content-200 line-clamp-3">
          {{ opportunity.description }}
        </p>
      </div>

      <div class="space-y-3 mb-4">
        <div class="flex justify-between items-center">
          <span class="text-sm text-content-300">Inversión mínima:</span>
          <span class="text-sm font-medium text-white">
            {{ formatCurrency(getMinimumInvestment(opportunity)) }}
          </span>
        </div>
        
        <div class="flex justify-between items-center">
          <span class="text-sm text-content-300">Plazo:</span>
          <span class="text-sm font-medium text-white">
            {{ opportunity.instrument === 'Co-ownership' ? 'Sin plazo definido' : opportunity.term }}
          </span>
        </div>

        <div v-if="opportunity.instrument === 'Debt'" class="flex justify-between items-center">
          <span class="text-sm text-content-300">Pago de intereses:</span>
          <span class="text-sm font-medium text-white">{{ opportunity.interestPayment }}</span>
        </div>

        <div class="flex justify-between items-center">
          <span class="text-sm text-content-300">Inversionistas:</span>
          <span class="text-sm font-medium text-white">{{ opportunity.investorCount }}</span>
        </div>
      </div>

      <div class="mb-4">
        <div class="flex justify-between items-center mb-2">
          <span class="text-sm text-content-300">Progreso</span>
          <span class="text-xs sm:text-sm font-medium text-white truncate ml-2">
            {{ formatCurrency(totalRaised) }} / {{ formatCurrency(opportunity.maxAmount) }}
          </span>
        </div>

        <div class="w-full bg-content-600 rounded-full h-3 relative overflow-hidden">
          <!-- Minimum goal indicator (red line) -->
          <div
            v-if="opportunity.minAmount && opportunity.minAmount > 0"
            class="absolute top-0 h-3 w-0.5 bg-red-500 z-10"
            :style="{ left: minAmountPercentage + '%' }"
            :title="'Meta mínima: ' + formatCurrency(opportunity.minAmount)"
          ></div>
          <!-- Intentions background (yellow) -->
          <div
            class="bg-yellow-400 h-3 rounded-full transition-all duration-500"
            :style="{ width: totalIntentionPercentage + '%' }"
          ></div>
          <!-- Closed amount (green) overlapping -->
          <div
            class="bg-green-500 h-3 rounded-full absolute top-0 left-0 transition-all duration-500"
            :style="{ width: closedPercentage + '%' }"
          ></div>
        </div>

        <div class="flex justify-between text-xs text-content-300 mt-1 gap-2">
          <span class="truncate">Int: {{ formatCurrency(opportunity.intentionAmount) }}</span>
          <span class="truncate">Cerr: {{ formatCurrency(opportunity.closedAmount) }}</span>
        </div>

        <!-- Minimum goal info -->
        <div v-if="opportunity.minAmount && opportunity.minAmount > 0" class="mt-2 p-2 rounded" :class="metMinimumGoal ? 'bg-green-900 bg-opacity-20' : 'bg-orange-900 bg-opacity-20'">
          <div class="flex items-center justify-between text-xs gap-2">
            <span class="text-content-300 truncate">Meta mínima:</span>
            <span :class="metMinimumGoal ? 'text-green-400 font-semibold' : 'text-orange-400'" class="whitespace-nowrap">
              {{ formatCurrency(opportunity.minAmount) }}
              <span v-if="metMinimumGoal" class="ml-1">✓</span>
            </span>
          </div>
          <p v-if="!metMinimumGoal" class="text-xs text-orange-400 mt-1">
            Faltan {{ formatCurrency(remainingToMinGoal) }}
          </p>
          <p v-else class="text-xs text-green-400 mt-1">
            ¡Meta alcanzada!
          </p>
        </div>
      </div>

      <div class="flex justify-between items-center mb-4">
        <div class="text-center">
          <div class="text-lg font-bold text-primary-500">{{ daysRemaining }}</div>
          <div class="text-xs text-content-300">días restantes</div>
        </div>
        <div class="text-center">
          <div class="text-lg font-bold text-green-500">{{ completionPercentage }}%</div>
          <div class="text-xs text-content-300">completado</div>
        </div>
        <div v-if="opportunity.estimatedIRR" class="text-center">
          <div class="text-lg font-bold text-blue-500">{{ opportunity.estimatedIRR }}%</div>
          <div class="text-xs text-content-300">TIR estimada</div>
        </div>
      </div>

      <!-- Investment Button -->
      <button
        class="w-full primary-button min-h-[44px] touch-manipulation"
        @click.stop="$emit('invest', opportunity)"
      >
        Invertir ahora
      </button>

      <!-- Existing Intentions Indicator -->
      <div v-if="opportunity.hasIntention" class="mt-2">
        <div class="flex items-center justify-center px-3 py-1.5 rounded-lg border"
             :class="getStatusBannerClass(opportunity.intentionStatus)">
          <div class="flex items-center space-x-2">
            <svg v-if="opportunity.intentionStatus === 'pending'" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <svg v-else-if="opportunity.intentionStatus === 'confirmed'" class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span class="text-xs font-semibold">
              Tienes intención {{ getStatusText(opportunity.intentionStatus).toLowerCase() }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'OpportunityCard',
  props: {
    opportunity: {
      type: Object,
      required: true
    }
  },
  emits: ['click', 'invest'],
  computed: {
    totalRaised() {
      const intentionAmount = parseFloat(this.opportunity.intentionAmount) || 0;
      const closedAmount = parseFloat(this.opportunity.closedAmount) || 0;
      return intentionAmount + closedAmount;
    },
    intentionPercentage() {
      const maxAmount = parseFloat(this.opportunity.maxAmount) || 0;
      if (maxAmount === 0) return 0;
      const intentionAmount = parseFloat(this.opportunity.intentionAmount) || 0;
      return Math.min((intentionAmount / maxAmount) * 100, 100);
    },
    totalIntentionPercentage() {
      const maxAmount = parseFloat(this.opportunity.maxAmount) || 0;
      if (maxAmount === 0) return 0;
      return Math.min((this.totalRaised / maxAmount) * 100, 100);
    },
    closedPercentage() {
      const maxAmount = parseFloat(this.opportunity.maxAmount) || 0;
      if (maxAmount === 0) return 0;
      const closedAmount = parseFloat(this.opportunity.closedAmount) || 0;
      return Math.min((closedAmount / maxAmount) * 100, 100);
    },
    completionPercentage() {
      const maxAmount = parseFloat(this.opportunity.maxAmount) || 0;
      if (maxAmount === 0) return 0;
      return Math.min(Math.round((this.totalRaised / maxAmount) * 100), 100);
    },
    daysRemaining() {
      if (!this.opportunity.deadline) return 0;
      const deadline = new Date(this.opportunity.deadline);
      const today = new Date();
      const diffTime = deadline - today;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return Math.max(diffDays, 0);
    },
    minAmountPercentage() {
      const maxAmount = parseFloat(this.opportunity.maxAmount) || 0;
      if (maxAmount === 0) return 0;
      const minAmount = parseFloat(this.opportunity.minAmount) || 0;
      return Math.min((minAmount / maxAmount) * 100, 100);
    },
    metMinimumGoal() {
      const minAmount = parseFloat(this.opportunity.minAmount) || 0;
      if (minAmount === 0) return true;
      return this.totalRaised >= minAmount;
    },
    remainingToMinGoal() {
      const minAmount = parseFloat(this.opportunity.minAmount) || 0;
      const remaining = minAmount - this.totalRaised;
      return Math.max(remaining, 0);
    }
  },
  methods: {
    formatCurrency(amount) {
      const numericAmount = parseFloat(amount) || 0;
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: 'MXN',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(numericAmount);
    },

    getStatusBannerClass(status) {
      const classes = {
        pending: 'bg-yellow-50 border-yellow-300 text-yellow-800',
        confirmed: 'bg-green-50 border-green-300 text-green-800'
      };
      return classes[status] || 'bg-gray-50 border-gray-300 text-gray-800';
    },

    getStatusText(status) {
      const texts = {
        pending: 'Intención Pendiente',
        confirmed: 'Intención Confirmada'
      };
      return texts[status] || 'Estado Desconocido';
    },

    getHelpText(status) {
      const texts = {
        pending: 'Tu intención está en revisión. Te notificaremos cuando tengamos una respuesta del administrador.',
        confirmed: 'Tu intención ha sido confirmada. Pronto recibirás más información sobre los siguientes pasos.'
      };
      return texts[status] || '';
    },

    getInstrumentLabel(instrument) {
      if (!instrument) return 'N/A';
      const labels = {
        'Debt': 'Deuda',
        'Co-ownership': 'Adquisición de inmueble'
      };
      return labels[instrument] || instrument;
    },

    getMinimumInvestment(opportunity) {
      // Return the appropriate minimum investment based on instrument type
      if (opportunity.instrument === 'Debt' && opportunity.minimumInvestmentDebt) {
        return opportunity.minimumInvestmentDebt;
      } else if (opportunity.instrument === 'Co-ownership' && opportunity.minimumInvestmentEquity) {
        return opportunity.minimumInvestmentEquity;
      }
      // Fallback to old field if exists
      return opportunity.minimumInvestment || 0;
    }
  }
}
</script>

<style scoped>
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.touch-manipulation {
  touch-action: manipulation;
}
</style>